import React, { useState } from 'react';
import { Button } from './ui/button';
import { useToast } from '../hooks/use-toast';
import { supabase } from '../integrations/supabase/client';
import { useAuth } from '../context/AuthContext';
import { Upload, Download, FileSpreadsheet, Check, X, Building, Tag, Share2 } from 'lucide-react';
import { generatePropertyCode } from '../utils/propertyCodeGenerator';
import * as XLSX from 'xlsx';

interface ImportedProperty {
  title: string;
  propertyType: string;
  transactionType: 'Venda' | 'Aluguel';
  description: string;
  listPrice: number;
  rentalPrice?: number;
  address: string;
  neighborhood: string;
  city: string;
  state: string;
  zipCode: string;
  bedrooms: number;
  bathrooms: number;
  suites: number;
  livingArea: number;
  garage: number;
  ownerName: string;
  ownerPhone: string;
  ownerEmail: string;
  condominium?: string;
  condominiumFee?: number;
  yearlyIptu?: number;
  // Campos para configuração
  isHighlighted?: boolean;
  sendToPortal?: boolean;
  condominiumId?: string;
}


interface BulkPropertyImportProps {
  onClose: () => void;
  onImportComplete: () => void;
}

export const BulkPropertyImport: React.FC<BulkPropertyImportProps> = ({ onClose, onImportComplete }) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [importedProperties, setImportedProperties] = useState<ImportedProperty[]>([]);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [propertyConfigs, setPropertyConfigs] = useState<Record<number, {
    isHighlighted: boolean;
    sendToPortal: boolean;
    condominiumId?: string;
  }>>({});
  const [condominiums, setCondominiums] = useState<Array<{ id: string; name: string }>>([]);
  
  const { user } = useAuth();
  const { toast } = useToast();

  React.useEffect(() => {
    loadCondominiums();
  }, []);

  const loadCondominiums = async () => {
    try {
      const { data, error } = await supabase
        .from('condominiums')
        .select('id, name')
        .eq('is_active', true)
        .order('name');

      if (error) throw error;
      setCondominiums(data || []);
    } catch (error) {
      console.error('Erro ao carregar condomínios:', error);
    }
  };

  const downloadTemplate = () => {
    const template = [
      {
        titulo: 'Apartamento 3 dormitórios',
        tipo_imovel: 'Apartamento',
        descricao: 'Amplo apartamento com 3 dormitórios...',
        endereco: 'Rua das Flores, 123',
        bairro: 'Cambuí',
        cidade: 'Campinas',
        estado: 'SP',
        cep: '13025-000',
        dormitorios: 3,
        banheiros: 2,
        suites: 1,
        area_util: 85,
        vagas_garagem: 1,
        preco_venda: 450000,
        preco_locacao: '',
        proprietario_nome: 'João Silva',
        proprietario_telefone: '(19) 99999-9999',
        proprietario_email: 'joao@email.com',
        condominio: '',
        valor_condominio: '',
        iptu_anual: 2500,
        observacoes: 'Imóvel em excelente estado'
      }
    ];

    const worksheet = XLSX.utils.json_to_sheet(template);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Imóveis');
    XLSX.writeFile(workbook, 'template_imoveis.xlsx');
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
        toast({
          title: "Formato inválido",
          description: "Por favor, selecione um arquivo Excel (.xlsx ou .xls)",
          variant: "destructive",
        });
        return;
      }
      setSelectedFile(file);
    }
  };

  const processImport = async () => {
    if (!selectedFile || !user) return;

    setLoading(true);
    try {
      const data = await selectedFile.arrayBuffer();
      const workbook = XLSX.read(data, { type: 'array' });
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const jsonData = XLSX.utils.sheet_to_json(worksheet);

      const processedProperties: ImportedProperty[] = [];
      
      for (const row of jsonData) {
        const rowData = row as any;
        
        // Determinar tipo de transação baseado nos preços
        let transactionType: 'Venda' | 'Aluguel' = 'Venda';
        if (rowData.preco_locacao && !rowData.preco_venda) {
          transactionType = 'Aluguel';
        } else if (rowData.preco_locacao && rowData.preco_venda) {
          transactionType = 'Venda'; // Priorizar venda se ambos existirem
        }

        const property: ImportedProperty = {
          title: rowData.titulo || '',
          propertyType: rowData.tipo_imovel || 'Apartamento',
          transactionType,
          description: rowData.descricao || '',
          listPrice: parseFloat(rowData.preco_venda) || 0,
          rentalPrice: rowData.preco_locacao ? parseFloat(rowData.preco_locacao) : undefined,
          address: rowData.endereco || '',
          neighborhood: rowData.bairro || '',
          city: rowData.cidade || '',
          state: rowData.estado || 'SP',
          zipCode: rowData.cep || '',
          bedrooms: parseInt(rowData.dormitorios) || 0,
          bathrooms: parseInt(rowData.banheiros) || 0,
          suites: parseInt(rowData.suites) || 0,
          livingArea: parseFloat(rowData.area_util) || 0,
          garage: parseInt(rowData.vagas_garagem) || 0,
          ownerName: rowData.proprietario_nome || '',
          ownerPhone: rowData.proprietario_telefone || '',
          ownerEmail: rowData.proprietario_email || '',
          condominium: rowData.condominio || '',
          condominiumFee: rowData.valor_condominio ? parseFloat(rowData.valor_condominio) : undefined,
          yearlyIptu: rowData.iptu_anual ? parseFloat(rowData.iptu_anual) : undefined,
        };

        processedProperties.push(property);
      }

      setImportedProperties(processedProperties);
      
      // Inicializar configurações padrão
      const configs: Record<number, any> = {};
      processedProperties.forEach((_, index) => {
        configs[index] = {
          isHighlighted: false,
          sendToPortal: true,
          condominiumId: undefined
        };
      });
      setPropertyConfigs(configs);
      
      setShowConfirmation(true);
      
    } catch (error) {
      console.error('Erro ao processar arquivo:', error);
      toast({
        title: "Erro",
        description: "Erro ao processar o arquivo Excel. Verifique se está no formato correto.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const updatePropertyConfig = (index: number, field: string, value: any) => {
    setPropertyConfigs(prev => ({
      ...prev,
      [index]: {
        ...prev[index],
        [field]: value
      }
    }));
  };

  const confirmImport = async () => {
    if (!user) return;

    setLoading(true);
    try {
      const propertiesToCreate = importedProperties.map((property, index) => {
        const config = propertyConfigs[index];
        const propertyCode = generatePropertyCode(property.propertyType);
        
        return {
          listing_id: propertyCode,
          title: property.title,
          property_type: property.propertyType,
          transaction_type: property.transactionType === 'Venda' ? 'sale' : property.transactionType === 'Aluguel' ? 'rent' : 'both',
          description: property.description,
          list_price: property.listPrice,
          rental_price: property.rentalPrice,
          address: property.address,
          neighborhood: property.neighborhood,
          city: property.city,
          state: property.state,
          zip_code: property.zipCode,
          bedrooms: property.bedrooms,
          bathrooms: property.bathrooms,
          suites: property.suites,
          area_total: property.livingArea,
          area_built: property.livingArea,
          garage_quantity: property.garage,
          owner_name: property.ownerName,
          owner_phone: property.ownerPhone,
          owner_email: property.ownerEmail,
          keys_location: 'Na imobiliária',
          complement: null,
          condominium_fee: property.condominiumFee,
          yearly_iptu: property.yearlyIptu,
          user_id: user.id,
          featured: config.isHighlighted,
          condominium_id: config.condominiumId,
          olx: {
            sendToOlx: config.sendToPortal,
            olxType: config.isHighlighted ? 'PREMIUM' : 'STANDARD'
          },
          images: []
        };
      });

      const { error } = await supabase
        .from('properties')
        .insert(propertiesToCreate);

      if (error) throw error;

      toast({
        title: "Importação concluída",
        description: `${importedProperties.length} imóveis foram importados com sucesso!`,
      });

      onImportComplete();
      onClose();
      
    } catch (error) {
      console.error('Erro ao importar imóveis:', error);
      toast({
        title: "Erro na importação",
        description: "Ocorreu um erro ao salvar os imóveis. Tente novamente.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  if (showConfirmation) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg w-full max-w-5xl h-[88vh] flex flex-col">
          <div className="p-4 border-b border-gray-200 flex-shrink-0">
            <h2 className="text-lg font-bold text-gray-900">
              Confirmar Importação - {importedProperties.length} Imóveis
            </h2>
            <p className="text-gray-600 mt-1 text-sm">
              Configure as opções para cada imóvel antes de finalizar a importação
            </p>
          </div>
          
          <div className="flex-1 overflow-y-auto p-4">
            <div className="space-y-4">
              {importedProperties.map((property, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-4">
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                    {/* Informações do Imóvel */}
                    <div className="lg:col-span-2">
                      <h3 className="font-semibold text-gray-900 mb-2">{property.title}</h3>
                      <div className="grid grid-cols-2 gap-2 text-sm text-gray-600">
                        <div>📍 {property.neighborhood}, {property.city}</div>
                        <div>🏠 {property.propertyType}</div>
                        <div>💰 {property.transactionType === 'Venda' 
                          ? `R$ ${property.listPrice.toLocaleString()}` 
                          : `R$ ${property.rentalPrice?.toLocaleString()}/mês`}
                        </div>
                        <div>🛏️ {property.bedrooms} dorm, {property.bathrooms} banh</div>
                      </div>
                    </div>
                    
                    {/* Configurações */}
                    <div className="space-y-3">
                      <div className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          id={`highlight-${index}`}
                          checked={propertyConfigs[index]?.isHighlighted || false}
                          onChange={(e) => updatePropertyConfig(index, 'isHighlighted', e.target.checked)}
                          className="w-4 h-4 text-orange-600"
                        />
                        <label htmlFor={`highlight-${index}`} className="text-sm flex items-center">
                          <Tag className="w-4 h-4 mr-1" />
                          Anúncio Destaque
                        </label>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          id={`portal-${index}`}
                          checked={propertyConfigs[index]?.sendToPortal !== false}
                          onChange={(e) => updatePropertyConfig(index, 'sendToPortal', e.target.checked)}
                          className="w-4 h-4 text-orange-600"
                        />
                        <label htmlFor={`portal-${index}`} className="text-sm flex items-center">
                          <Share2 className="w-4 h-4 mr-1" />
                          Enviar para Portal
                        </label>
                      </div>
                      
                      <div>
                        <label className="text-sm text-gray-700 flex items-center mb-1">
                          <Building className="w-4 h-4 mr-1" />
                          Condomínio
                        </label>
                        <select
                          value={propertyConfigs[index]?.condominiumId || ''}
                          onChange={(e) => updatePropertyConfig(index, 'condominiumId', e.target.value || undefined)}
                          className="w-full text-sm border border-gray-300 rounded px-2 py-1"
                        >
                          <option value="">Selecionar condomínio</option>
                          {condominiums.map(condo => (
                            <option key={condo.id} value={condo.id}>{condo.name}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          <div className="p-4 border-t border-gray-200 flex justify-end space-x-3 flex-shrink-0">
            <Button variant="outline" onClick={() => setShowConfirmation(false)}>
              Voltar
            </Button>
            <Button onClick={confirmImport} disabled={loading}>
              {loading ? 'Importando...' : 'Confirmar Importação'}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[85vh] flex flex-col">
        <div className="p-4 border-b border-gray-200 flex-shrink-0">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-bold text-gray-900">Cadastrar Imóveis em Lote</h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>
        
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Download Template */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <FileSpreadsheet className="w-6 h-6 text-blue-600 mt-0.5" />
              <div className="flex-1">
                <h3 className="font-semibold text-blue-900">Template Excel</h3>
                <p className="text-blue-700 text-sm mt-1">
                  Baixe o template para preencher os dados dos imóveis no formato correto.
                </p>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={downloadTemplate}
                  className="mt-2 border-blue-300 text-blue-600 hover:bg-blue-100"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Baixar Template
                </Button>
              </div>
            </div>
          </div>

          {/* Instruções */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="font-semibold text-gray-900 mb-2">Instruções:</h3>
            <ul className="text-sm text-gray-700 space-y-1">
              <li>• Preencha o arquivo Excel com os dados dos imóveis</li>
              <li>• O sistema identificará automaticamente se é venda ou locação baseado nos preços</li>
              <li>• Campos obrigatórios: título, tipo, endereço, bairro, cidade, proprietário</li>
              <li>• Use preço_venda para imóveis à venda e preco_locacao para locação</li>
              <li>• Na próxima tela você poderá configurar destaque, portal e condomínio</li>
            </ul>
          </div>

          {/* Upload Area */}
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
            <div className="space-y-4">
              <Upload className="w-12 h-12 text-gray-400 mx-auto" />
              <div>
                <label className="cursor-pointer">
                  <input
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                  <span className="text-lg text-gray-600">
                    Clique para selecionar o arquivo Excel
                  </span>
                </label>
                <p className="text-sm text-gray-500 mt-1">
                  Formatos aceitos: .xlsx, .xls
                </p>
              </div>
            </div>
          </div>

          {/* Selected File */}
          {selectedFile && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center space-x-3">
                <Check className="w-6 h-6 text-green-600" />
                <div>
                  <p className="font-medium text-green-900">{selectedFile.name}</p>
                  <p className="text-sm text-green-700">
                    Arquivo selecionado ({(selectedFile.size / 1024).toFixed(1)} KB)
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
        
        <div className="p-4 border-t border-gray-200 flex justify-end space-x-3 flex-shrink-0">
          <Button variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button 
            onClick={processImport} 
            disabled={!selectedFile || loading}
          >
            {loading ? 'Processando...' : 'Processar Arquivo'}
          </Button>
        </div>
      </div>
    </div>
  );
};